<template>
  <div
    v-click-outside="clickedOutside"
    :class="computedStyleClasses"
    :tabindex="tabindex"
    @keyup.enter.stop="toggle"
    @keyup.esc.stop="closeDropDown"
    @keyup.down.stop="arrowDown"
    @keyup.up.stop="arrowUp"
  >
    <div
      ref="trigger"
      aria-haspopup="true"
      aria-expanded="false"
      :aria-describedby="ariaDescribedBy"
      :class="computedClasses"
      @click="toggle"
    >
      <slot name="trigger"></slot>
    </div>
    <slot :expand="expand">
      <ul
        v-if="expand"
        class="ips-dropdown-menu dropdown-menu"
        :class="{
          'dropdown-menu-end': dropRight,
          'overflow-auto': overflowScroll,
          'overflow-hidden': overflowHidden,
        }"
      >
        <slot name="items" :close="closeDropDown" :open="openDropDown"></slot>
      </ul>
    </slot>
  </div>
</template>

<script>
import vClickOutside from 'v-click-outside';

export default {
  name: 'IpsDropdown',
  directives: {
    ClickOutside: vClickOutside.directive,
  },
  props: {
    ariaDescribedBy: {
      name: 'aria-describedby',
      description: 'Add the aria-describedby property. Important for accessibility',
      type: String,
      default: '',
      required: false,
    },
    classes: {
      name: 'classes',
      description: 'Add additional classes to the trigger',
      type: Array,
      default() {
        return [];
      },
      required: false,
    },
    disabled: {
      name: 'disabled',
      description: 'Disabled the dropdown',
      type: Boolean,
      default: false,
      required: false,
    },
    disableOutsideClick: {
      name: 'disableOutsideClick',
      description: 'Disable native behaviour when clicking outside dropdown',
      type: Boolean,
      default: false,
      required: false,
    },
    dropRight: {
      name: 'dropRight',
      description: 'Right align the dropdown list to the parent',
      type: Boolean,
      default: false,
      required: false,
    },
    expanded: {
      name: 'expanded',
      description: 'Set the initial state of the dropdown on render',
      type: Boolean,
      default: false,
      required: false,
    },
    tabindex: {
      name: 'tabindex',
      description: 'Add a tab index. Important for accessibility',
      type: Number,
      default: 0,
      required: false,
    },
    onlyOpen: {
      name: 'onlyOpen',
      description: 'Does not close dropdown when toggle is called',
      type: Boolean,
      default: false,
      required: false,
    },
    overflowScroll: {
      name: 'overflowScroll',
      description: 'Scrolling when dropdown content overflows',
      type: Boolean,
      default: false,
      required: false,
    },
    overflowHidden: {
      name: 'overflowHidden',
      description: 'Hide overflow',
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      expand: this.expanded,
    };
  },
  computed: {
    computedClasses() {
      const classes = ['ips', 'dropdown-trigger'];
      if (this.classes.length > 0) {
        this.classes.forEach((item) => {
          classes.push(item);
        });
      }
      return classes;
    },
    computedStyleClasses() {
      const classesArray = ['dropdown'];
      if (this.disabled) classesArray.push('disabled');
      if (this.classes.indexOf('dropup') > 0) classesArray.push('dropup');
      if (this.classes.indexOf('dropend') > 0) classesArray.push('dropend');
      if (this.classes.indexOf('dropstart') > 0) classesArray.push('dropstart');
      return classesArray;
    },
  },
  watch: {
    expanded: {
      immediate: true,
      handler(newVal) {
        this.$nextTick(() => {
          this.expand = newVal;
        });
      },
    },
  },
  methods: {
    openDropDown() {
      this.expand = true;
    },
    closeDropDown() {
      this.expand = false;
    },
    arrowDown() {
      const dropdownItems = Array.from(document.getElementsByClassName('dropdown-item'));
      const index = dropdownItems.indexOf(document.activeElement);
      if (index < dropdownItems.length - 1) {
        dropdownItems[index + 1].focus();
      }
    },
    arrowUp() {
      const dropdownItems = Array.from(document.getElementsByClassName('dropdown-item'));
      const index = dropdownItems.indexOf(document.activeElement);
      if (index > 0) {
        dropdownItems[index - 1].focus();
      }
    },
    toggle() {
      if (this.disabled) return;
      if (this.onlyOpen) {
        this.expand = true;
      } else {
        this.expand = !this.expand;
      }
      this.$refs.trigger.setAttribute('aria-expanded', this.expand);
      this.$emit('toggle', { toggle: this.expand });
    },
    clickedOutside() {
      if (!this.disableOutsideClick) {
        this.$emit('clicked-outside');
        this.closeDropDown();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'IpsDropdown';
</style>
